import React from 'react';

/** Сар бүрийн төлбөрийн тор — нээлттэй ба админ хуудас ХУВААЛЦДАГ цэвэр компонент. */
export type Household = { id: string; number: string; type: string; monthly_fee: number; owner_name?: string; phone?: string; email?: string; balance?: number; balance_source?: 'bpay' | 'manual' | 'none' | 'garage'; bpay_synced_at?: string | null; owner?: string };
export type Pay = { household_id: string; month: number; amount: number; paid_at: string | null; note: string; source?: string; bank_report?: boolean; bank_reported?: boolean; receipt?: { id: string; status: string; ddtd: string; printed_at: string | null; emailed_at: string | null; email_to: string } | null };
export type MonthState = 'paid' | 'partial' | 'unpaid' | 'future' | 'before';

export const TYPE_LABEL: Record<string, string> = { apartment: 'Орон сууц', townhouse: 'Таунхаус', house: 'Single хаус', parking: 'Граж' };
export const TYPE_ORDER = ['apartment', 'townhouse', 'house', 'parking'];
export const C = {
  paid: { bg: '#e3efe3', fg: '#2f7a4a' }, partial: { bg: '#fbd9d2', fg: '#9f2f1f' }, unpaid: { bg: '#f8e4df', fg: '#b3412f' },
  future: { bg: '#f3efe6', fg: '#9a9385' }, before: { bg: '#e9e3d6', fg: '#8a8375' }, mine: '#fff6dc',
};
export const fmtMnt = (n: number) => (n || 0).toLocaleString('en-US') + ' ₮';
/** Нүдэнд багтах богино дүн: 220000 → 220k, 1380000 → 1.38M */
export const fmtShort = (n: number) => { const a = Math.abs(n); return a >= 1e6 ? (a / 1e6).toFixed(a % 1e6 ? 2 : 0).replace(/\.?0+$/, '') + 'M' : a >= 1000 ? Math.round(a / 1000) + 'k' : String(a); };
/** Сар төлөгдсөн эсэх: төлсөн дүн сарын дүнд хүрсэн (сарын дүн 0 бол бичлэг байхад л төлсөн) */
export const settled = (pay: Pay | undefined, fee: number) => !!pay && (!fee || (pay.amount || 0) >= fee);
const collator = new Intl.Collator('mn', { numeric: true });

export function periodState(year: number, month: number, feeStart: string, now: { y: number; m: number }): 'due' | 'future' | 'before' {
  if (feeStart && /^\d{4}-\d{2}$/.test(feeStart)) {
    const fy = +feeStart.slice(0, 4), fm = +feeStart.slice(5, 7);
    if (year < fy || (year === fy && month < fm)) return 'before';
  }
  if (year > now.y || (year === now.y && month > now.m)) return 'future';
  return 'due';
}

export type Row = { h: Household; pays: Map<number, Pay>; paidCount: number; dueMonths: number[]; dueAmount: number; mine: boolean };
// dueMonths = хугацаа болсон, төлсөн дүн нь сарын дүнд хүрээгүй сарууд; dueAmount = хугацаа болсон сар × дүн − бүх төлсөн дүн (сөрөг = урьдчилгаа)

export function computeRows(o: {
  households: Household[]; payments: Pay[]; year: number; feeStart: string;
  filterType?: string; search?: string; onlyUnpaid?: boolean; highlightId?: string | null; now?: { y: number; m: number };
}): { rows: Row[]; dueMonths: number[]; footer: { paid: number; total: number }[] } {
  const now = o.now || { y: new Date().getFullYear(), m: new Date().getMonth() + 1 };
  const byH = new Map<string, Map<number, Pay>>();
  for (const p of o.payments) {
    if (!byH.has(p.household_id)) byH.set(p.household_id, new Map());
    byH.get(p.household_id)!.set(p.month, p);
  }
  const dueMonths: number[] = [];
  for (let m = 1; m <= 12; m++) if (periodState(o.year, m, o.feeStart, now) === 'due') dueMonths.push(m);
  const q = (o.search || '').trim().toLowerCase();
  let rows: Row[] = o.households
    .filter(h => !o.filterType || h.type === o.filterType)
    .filter(h => !q || h.number.toLowerCase().includes(q))
    .map(h => {
      const pays = byH.get(h.id) || new Map<number, Pay>();
      const fee = h.monthly_fee || 0;
      const due = dueMonths.filter(m => !settled(pays.get(m), fee));
      const paidSum = Array.from(pays.values()).reduce((a, p) => a + (p.amount || 0), 0);
      // СӨХ eBill-тэй холбогдсон бол «Дутуу дүн» = Төрийн банкнаас ирсэн эцсийн үлдэгдэл; үгүй бол тооцоо: хугацаа болсон сар × дүн − төлсөн дүн
      const dueAmount = (h.balance_source === 'bpay' || h.balance_source === 'garage') ? (h.balance || 0) : h.balance_source === 'none' ? 0 : (fee ? dueMonths.length * fee - paidSum : 0);
      const paidCount = Array.from(pays.values()).filter(p => settled(p, fee)).length;
      return { h, pays, paidCount, dueMonths: due, dueAmount, mine: !!o.highlightId && h.id === o.highlightId };
    });
  if (o.onlyUnpaid) rows = rows.filter(r => r.dueMonths.length > 0 || r.dueAmount > 0);
  rows.sort((a, b) => {
    if (a.mine !== b.mine) return a.mine ? -1 : 1;
    const ta = TYPE_ORDER.indexOf(a.h.type), tb = TYPE_ORDER.indexOf(b.h.type);
    if (ta !== tb) return (ta < 0 ? 9 : ta) - (tb < 0 ? 9 : tb);
    return collator.compare(a.h.number, b.h.number);
  });
  const footer = Array.from({ length: 12 }, (_, i) => ({ paid: rows.filter(r => settled(r.pays.get(i + 1), r.h.monthly_fee || 0)).length, total: rows.length }));
  return { rows, dueMonths, footer };
}

type Props = {
  households: Household[]; payments: Pay[]; year: number; feeStart: string;
  filterType?: string; search?: string; onlyUnpaid?: boolean; highlightId?: string | null;
  editable?: boolean; showOwner?: boolean; mobile?: boolean; pending?: Set<string>; balanceHeader?: string;
  onToggle?: (h: Household, month: number, paid: boolean, shift: boolean) => void;
};

export default function PaymentGrid(p: Props) {
  const now = { y: new Date().getFullYear(), m: new Date().getMonth() + 1 };
  const { rows, footer } = computeRows({ ...p, now });
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const cellW = p.mobile ? 38 : 50, cellH = p.mobile ? 36 : 34;
  const isCur = (m: number) => p.year === now.y && m === now.m;
  const th: React.CSSProperties = { position: 'sticky', top: 0, background: '#faf8f2', zIndex: 2, padding: '8px 6px', fontSize: 12, color: '#5f6f66', fontWeight: 600, borderBottom: '1px solid #e8e5dc', textAlign: 'center', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: 0, borderBottom: '1px solid #f0ece2', textAlign: 'center', fontVariantNumeric: 'tabular-nums' };
  const stickyL: React.CSSProperties = { position: 'sticky', left: 0, zIndex: 1, textAlign: 'left', padding: '0 10px', fontWeight: 700, whiteSpace: 'nowrap' };

  return (
    <div style={{ maxHeight: p.mobile ? '72vh' : '70vh', overflow: 'auto', border: '1px solid #e8e5dc', borderRadius: 12, background: '#fff' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: 13.5 }}>
        <thead><tr>
          <th style={{ ...th, ...stickyL, zIndex: 3, background: '#faf8f2' }}>Айл</th>
          {!p.mobile && <th style={th}>Төрөл</th>}
          {p.showOwner && !p.mobile && <th style={{ ...th, textAlign: 'left' }}>Холбоо</th>}
          {months.map(m => <th key={m} style={{ ...th, minWidth: cellW, outline: isCur(m) ? '2px solid #236844' : undefined, outlineOffset: -2 }}>{m}</th>)}
          <th style={th}>Төлсөн</th><th style={th}>Дутуу</th><th style={{ ...th, textAlign: 'right' }}>{p.balanceHeader || 'Дутуу дүн'}</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={16} style={{ padding: 18, textAlign: 'center', color: '#8a978f' }}>Айл олдсонгүй</td></tr>}
          {rows.map(r => {
            const rowBg = r.mine ? C.mine : '#fff';
            return (
              <tr key={r.h.id} style={{ background: rowBg }}>
                <td style={{ ...td, ...stickyL, background: rowBg, height: cellH }}>{r.h.number}{r.h.owner && <div style={{ fontSize: 11, fontWeight: 500, color: '#5f6f66', whiteSpace: 'nowrap' }} title="Гражийн эзэмшигч">{r.h.owner}</div>}</td>
                {!p.mobile && <td style={{ ...td, padding: '0 6px' }}><span style={{ fontSize: 11, background: '#eef1ee', color: '#3a4a41', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>{TYPE_LABEL[r.h.type] || r.h.type}</span></td>}
                {p.showOwner && !p.mobile && <td style={{ ...td, textAlign: 'left', padding: '0 8px', fontSize: 12, color: '#5f6f66', whiteSpace: 'nowrap' }}>{[r.h.phone, r.h.email].filter(Boolean).join(' · ') || '—'}</td>}
                {months.map(m => {
                  const pay = r.pays.get(m);
                  const fee = r.h.monthly_fee || 0;
                  const st = periodState(p.year, m, p.feeStart, now);
                  const diff = pay ? fee - (pay.amount || 0) : 0;          // >0 дутуу, <0 илүү/урьдчилгаа
                  const state: MonthState = pay ? (settled(pay, fee) ? 'paid' : 'partial') : st === 'due' ? 'unpaid' : st;
                  const col = C[state];
                  const sym = state === 'paid' ? (diff < 0 ? <span>✓<span style={{ display: 'block', fontSize: 9.5, fontWeight: 600, lineHeight: 1 }}>+{fmtShort(-diff)}</span></span> : '✓')
                    : state === 'partial' ? <span style={{ fontSize: 11.5 }}>−{fmtShort(diff)}</span> : state === 'unpaid' ? '✕' : state === 'future' ? '·' : '–';
                  const title = pay
                    ? `${r.h.number} · ${m}-р сар: төлсөн ${fmtMnt(pay.amount)} / ${fmtMnt(fee)}${diff > 0 ? ` — дутуу ${fmtMnt(diff)}` : diff < 0 ? ` — илүү ${fmtMnt(-diff)} (урьдчилгаа)` : ''}${pay.paid_at ? ` (${pay.paid_at})` : ''}${pay.note ? ` — ${pay.note}` : ''}`
                    : `${r.h.number} · ${m}-р сар: ${state === 'unpaid' ? `төлөөгүй (${fmtMnt(fee)})` : state === 'future' ? 'хугацаа болоогүй' : 'хураамж эхлээгүй'}`;
                  const key = `${r.h.id}:${m}`;
                  const isPending = p.pending?.has(key);
                  return (
                    <td key={m} title={title}
                      onClick={p.editable && p.onToggle ? (e) => p.onToggle!(r.h, m, !!pay, e.shiftKey) : undefined}
                      style={{ ...td, background: col.bg, color: col.fg, fontWeight: 700, height: cellH, minWidth: cellW,
                        cursor: p.editable ? 'pointer' : 'default', opacity: isPending ? 0.4 : 1,
                        outline: isCur(m) ? '2px solid #236844' : undefined, outlineOffset: -2, userSelect: 'none' }}>
                      {sym}
                    </td>
                  );
                })}
                <td style={{ ...td, fontWeight: 600 }}>{r.paidCount}</td>
                <td style={{ ...td, fontWeight: 800, color: r.dueMonths.length ? '#b3412f' : '#8a978f' }}>{r.dueMonths.length || '—'}</td>
                <td style={{ ...td, textAlign: 'right', padding: '0 10px', fontWeight: 600, color: r.dueAmount > 0 ? '#b3412f' : r.dueAmount < 0 ? '#2f7a4a' : '#8a978f', whiteSpace: 'nowrap' }}
                  title={r.h.balance_source === 'bpay' ? `Төрийн банкны СӨХ нэхэмжлэх · шинэчлэгдсэн ${r.h.bpay_synced_at ? r.h.bpay_synced_at.slice(0, 16).replace('T', ' ') : '—'}` : r.h.balance_source === 'garage' ? 'Гражийн үлдэгдэл — сар бүрийн 5-нд тариф нэмэгдэж, төлбөрөөр хасагдана (Орон сууц → граж → Төлбөр)' : r.h.balance_source === 'none' ? 'Төрийн банктай холбогдоогүй айл' : undefined}>
                  {r.h.balance_source === 'none' ? '—' : r.dueAmount > 0 ? fmtMnt(r.dueAmount) : r.dueAmount < 0 ? <span title="Урьдчилж / илүү төлсөн">+{fmtMnt(-r.dueAmount)}</span> : '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot><tr style={{ background: '#faf8f2', fontWeight: 700 }}>
          <td style={{ ...td, ...stickyL, background: '#faf8f2', height: cellH, fontSize: 12 }}>Төлсөн/нийт</td>
          {!p.mobile && <td style={td}></td>}
          {p.showOwner && !p.mobile && <td style={td}></td>}
          {months.map(m => { const st = periodState(p.year, m, p.feeStart, now); return <td key={m} style={{ ...td, fontSize: 11.5, color: '#3a4a41' }}>{st === 'due' ? `${footer[m - 1].paid}/${footer[m - 1].total}` : ''}</td>; })}
          <td style={td} colSpan={3}></td>
        </tr></tfoot>
      </table>
    </div>
  );
}

export function Legend() {
  const items: [string, string, string][] = [['Төлсөн', C.paid.bg, C.paid.fg], ['Дутуу төлсөн', C.partial.bg, C.partial.fg], ['Төлөөгүй', C.unpaid.bg, C.unpaid.fg], ['Хугацаа болоогүй', C.future.bg, C.future.fg], ['Хураамж эхлээгүй', C.before.bg, C.before.fg], ['Манайх', C.mine, '#8a6d1d']];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12.5, color: '#5f6f66' }}>
      {items.map(([l, bg, fg]) => <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 14, borderRadius: 3, background: bg, border: `1px solid ${fg}` }} />{l}</span>)}
    </div>
  );
}
